import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { MaritalStatusesService, type MaritalStatus } from '@/services/modules/MaritalStatusesService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

export function SettingsMaritalStatusesPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.marital_statuses.create')
  const canEdit = can('settings.marital_statuses.edit')
  const canDelete = can('settings.marital_statuses.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar estado civil' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(MaritalStatusesService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: MaritalStatus }>({ open: false })
  const [openDelete, setOpenDelete] = useState<MaritalStatus | null>(null)

  const exp = useDataTableExport({
    filename: 'estados-civis',
    service: MaritalStatusesService,
    filters: sf.filters,
    columns: [
      { header: 'Descrição', accessor: (m: MaritalStatus) => m.description },
      { header: 'Status',    accessor: (m: MaritalStatus) => m.status },
    ],
  })

  async function handleSave(payload: Partial<MaritalStatus>) {
    try {
      if (openForm.item) { await MaritalStatusesService.update(openForm.item.id, payload); toast.success('Estado civil atualizado.') }
      else { await MaritalStatusesService.create(payload); toast.success('Estado civil criado.') }
      setOpenForm({ open: false }); setReload(v => v + 1)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.') }
  }
  async function handleDelete() {
    if (!openDelete) return
    try { await MaritalStatusesService.destroy(openDelete.id); toast.success('Estado civil removido.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  // Sem coluna código. Primeira esquerda; demais center; ações vão à direita (dropdown).
  const columns: DataTableColumn<MaritalStatus>[] = [
    { key: 'description', header: 'Descrição', sortable: true, alwaysVisible: true, render: m => <span className="font-medium text-gray-800">{m.description}</span> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: m => (
        <StatusDot value={m.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Estados civis"
        subtitle="Catálogo oficial"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Workflow' }, { label: 'Estados civis' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo estado civil</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.workflow.maritalstatuses"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={m => m.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(m) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: m }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(m) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <MaritalStatusForm open={openForm.open} item={openForm.item} onClose={() => setOpenForm({ open: false })} onSave={handleSave} />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover estado civil?"
        description={`Tem certeza que deseja remover "${openDelete?.description}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function MaritalStatusForm({ open, item, onClose, onSave }: {
  open: boolean; item?: MaritalStatus; onClose: () => void
  onSave: (payload: Partial<MaritalStatus>) => Promise<void>
}) {
  const [maritalStatus, setMaritalStatus] = useState('')
  const [description, setDescription] = useState('')
  const [portoSeguroReport, setPortoSeguroReport] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setMaritalStatus(item?.maritalStatus ?? '')
    setDescription(item?.description ?? '')
    setPortoSeguroReport(item?.portoSeguroReport ?? '')
    setStatus(item?.status ?? 'ATIVO')
  }, [open, item])

  async function submit() {
    setSaving(true)
    await onSave({ maritalStatus, description, portoSeguroReport: portoSeguroReport.trim() || null, status } as Partial<MaritalStatus>)
    setSaving(false)
  }
  return (
    <Modal open={open} onClose={onClose}
      title={item ? 'Editar estado civil' : 'Novo estado civil'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!description.trim() || !maritalStatus.trim()}>Salvar</Button></>}
    >
      <div className="space-y-3">
        <Input label="Sigla"     required value={maritalStatus} onChange={e => setMaritalStatus(e.target.value.toUpperCase())} maxLength={5} placeholder="C, S, D" />
        <Input label="Descrição" required value={description}   onChange={e => setDescription(e.target.value)} placeholder="Casado(a)" />
        <Input
          label="Código Porto Seguro"
          value={portoSeguroReport}
          onChange={e => setPortoSeguroReport(e.target.value)}
          placeholder="Código do estado civil no layout da Porto Seguro"
          hint="Usado no relatório da Porto Seguro. Sem este código, o assinante é descartado do arquivo da Porto."
        />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
